import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';
import { mapVoiceCampaign } from './voiceCampaigns';
import type { VoiceCampaignRow } from './voiceCampaigns';

// ── Campaign extras ───────────────────────────────────────────────────

/** `GET /api/campaigns/{campaign_id}` — single campaign detail. */
export async function fetchVoiceCampaign(id: string, axiosInstance?: AxiosInstance): Promise<VoiceCampaignRow | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.campaigns}/${id}`;
  const response = await client.get<any>(url);
  return response.data ? mapVoiceCampaign(response.data) : null;
}

/** Parsed body from `POST /api/campaigns/{campaign_id}/audience`. */
export type CampaignAudienceUploadResult = {
  ok: boolean;
  total_uploaded: number;
  valid_phones: number;
  duplicates: number;
  dnc_blocked: number;
  final_audience_count: number;
  sample?: string[];
};

function parseCampaignAudienceUploadBody(data: unknown): Omit<CampaignAudienceUploadResult, 'ok'> {
  const d = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const nested = d.data && typeof d.data === 'object' ? (d.data as Record<string, unknown>) : d;
  const n = (x: unknown) => {
    const v = Number(x);
    return Number.isFinite(v) ? v : 0;
  };
  const sampleRaw = nested.sample;
  const sample = Array.isArray(sampleRaw)
    ? sampleRaw.map((x) => String(x))
    : undefined;
  return {
    total_uploaded: n(nested.total_uploaded ?? nested.total_rows),
    valid_phones: n(nested.valid_phones),
    duplicates: n(nested.duplicates),
    dnc_blocked: n(nested.dnc_blocked),
    final_audience_count: n(
      nested.final_audience_count ?? nested.audience_count ?? nested.count ?? nested.rows ?? nested.valid_phones,
    ),
    sample,
  };
}

/** `DELETE /api/campaigns/{campaign_id}/audience` — remove all audience contacts from a campaign. */
export async function deleteCampaignAudience(
  campaignId: string,
  axiosInstance?: AxiosInstance,
): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.campaigns}/${campaignId}/audience`;
  const response = await client.delete(url);
  return response.status === 200 || response.status === 204;
}

/** `POST /api/campaigns/{campaign_id}/audience` — upload CSV audience list. */
export async function uploadCampaignAudience(
  campaignId: string,
  csvFile: File,
  axiosInstance?: AxiosInstance,
): Promise<CampaignAudienceUploadResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.campaigns}/${campaignId}/audience`;
  const formData = new FormData();
  formData.append('file', csvFile);
  const response = await client.post<unknown>(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  return { ok: true, ...parseCampaignAudienceUploadBody(response.data) };
}
